import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  HostListener,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  MobileNavService,
  MobileTab,
} from '../../../shared/mobile/mobile-bottom-nav/mobile-nav.service';
import {
  RawMaterial,
  LabourItem,
  IndirectLabourItem,
  FixedItem,
  VariableOverhead,
} from '../raw-materials-component/raw-materials.component';
import {
  CostItemOffcanvasComponent,
  CostItemEditData,
} from './cost-item-offcanvas/cost-item-offcanvas.component';
import { AivorInsightsComponent } from '../aivor-insights/aivor-insights.component';
import { DonutChartComponent } from '../donut-chart-component/donut-chart.component';
import { CostBreakdownComponent } from '../cost-breakdown-component/cost-breakdown.component';
import { CombinedCostItem } from '../raw-materials-component/raw-materials.component';
import { MobileMetricRibbonComponent, MobileMetric } from '../../../shared/mobile/mobile-metric-ribbon/mobile-metric-ribbon.component';
import { MobileDataCardComponent, MobileCardField } from '../../../shared/mobile/mobile-data-card/mobile-data-card.component';
import { SingleSelectDropdownComponent } from '../../../UI/business-details/single-select-dropdown.component';

@Component({
  selector: 'app-mobile-calc-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MobileMetricRibbonComponent,
    MobileDataCardComponent,
    SingleSelectDropdownComponent,
    CostItemOffcanvasComponent,
    AivorInsightsComponent,
    DonutChartComponent,
    CostBreakdownComponent,
  ],
  templateUrl: './mobile-calc-view.component.html',
  styleUrls: ['./mobile-calc-view.component.css'],
})
export class MobileCalcViewComponent implements OnInit, OnDestroy, OnChanges {
  @Input() rawMaterials: RawMaterial[] = [];
  @Input() directLabor: LabourItem[] = [];
  @Input() indirectLabor: IndirectLabourItem[] = [];
  @Input() fixedOverheads: FixedItem[] = [];
  @Input() variableOverheads: VariableOverhead[] = [];
  @Input() employees: any[] = [];
  @Input() fixedOverheadNames: any[] = [];
  @Input() variableOverheadItems: any[] = [];
  @Input() materialNames: any[] = [];
  @Input() accountType = 'full';
  @Input() uomList: any[] = [];
  @Input() combinedCostItems: CombinedCostItem[] = [];
  @Input() totalCost = 0;
  @Input() sellingPrice = 0;
  @Input() aiSuggestedPrice = 0;
  @Input() totalCostWithProfit = 0;
  @Input() profitMargin = 0;
  @Input() profitMarginPercentage = 0;
  @Input() isLoading = false;
  @Input() fullName = '';
  @Input() tenantName = '';
  @Input() allProductCosts: Record<string, CombinedCostItem[]> = {};
  @Input() tenantProfile: any = null;
  @Input() competitors: any[] = [];
  @Input() markupPercent = 0;
  @Input() selectedProductName = '';
  @Input() productName = ''; // Alias for selectedProductName
  @Input() products: any[] = []; // Product list for dropdown
  @Input() calculatorSellingPrice = 0;
  @Input() markupPrice = 0;
  @Input() suggestedSellingPriceRaw = 0;
  @Input() productSellingPrice = 0;
  @Input() targetTotalCost: number | null = null;
  @Input() serviceLabourNeedsRate = false;

  @Input() mobileView: 'calculator' | 'summary' | 'graphs' | 'aivor' = 'calculator';
  @Input() activeProductId: number | null = null;

  @Input() mobileBusy = false;
  @Input() mobileBusyLabel = '';
  @Input() fohBatchChangePending = false;

  showTotalCostAlert = false;

  @Output() productChange = new EventEmitter<number>();
  @Output() marginChange = new EventEmitter<number>();
  @Output() saveAll = new EventEmitter<void>();
  @Output() fohSaveAll = new EventEmitter<void>();
  @Output() costItemUpsert = new EventEmitter<CostItemEditData>();
  @Output() costItemDelete = new EventEmitter<CostItemEditData>();
  @Output() sellingPriceChange = new EventEmitter<number>();
  @Output() addProduct = new EventEmitter<void>();
  @Output() targetCostChange = new EventEmitter<number | null>();

  readonly String = String;

  get productOptions() {
    return this.products.map((p: any) => ({
      value: String(p.itemID),
      label: p.itemName + (p.itemTypeID === 4 ? ' (Service)' : p.isBuyout ? ' (Buy Out)' : ''),
    }));
  }

  selectedProductId: number | null = null;
  activeTab: MobileTab = 'materials';
  offcanvasVisible = false;
  selectedItem: CostItemEditData | null = null;

  editingSellingPrice = false;
  editSellingPriceValue = 0;

  // Delete sheet state
  deleteSheetOpen = false;
  pendingDeleteCard: { itemName: string; unitCost: number; category: string; fullItem: any } | null = null;
  deleteSwipeDeltaY = 0;
  deleteSwipeActive = false;
  private deleteSwipeStartY = 0;
  private deleteSwipeSheetHeight = 0;

  get deleteSwipeProgress(): number {
    if (this.deleteSwipeSheetHeight === 0) return 0;
    return Math.min(1, this.deleteSwipeDeltaY / (this.deleteSwipeSheetHeight * 0.3));
  }

  onDeleteHandleTouchStart(e: TouchEvent): void {
    this.deleteSwipeActive = true;
    this.deleteSwipeStartY = e.touches[0].clientY;
    const sheet = (e.currentTarget as HTMLElement).closest('.m-del-sheet') as HTMLElement;
    this.deleteSwipeSheetHeight = sheet ? sheet.offsetHeight : 300;
    this.deleteSwipeDeltaY = 0;
  }

  onDeleteHandleTouchMove(e: TouchEvent): void {
    if (!this.deleteSwipeActive) return;
    const delta = e.touches[0].clientY - this.deleteSwipeStartY;
    this.deleteSwipeDeltaY = Math.max(0, delta);
  }

  onDeleteHandleTouchEnd(): void {
    if (!this.deleteSwipeActive) return;
    this.deleteSwipeActive = false;
    if (this.deleteSwipeDeltaY > this.deleteSwipeSheetHeight * 0.3) {
      this.deleteSwipeDeltaY = 0;
      this.closeDeleteSheet();
    } else {
      this.deleteSwipeDeltaY = 0;
    }
  }

  onRequestDelete(card: { itemName: string; unitCost: number; category: string; fullItem: any }): void {
    this.pendingDeleteCard = card;
    this.deleteSwipeDeltaY = 0;
    this.deleteSwipeActive = false;
    this.deleteSheetOpen = true;
  }

  closeDeleteSheet(): void {
    this.deleteSheetOpen = false;
    this.pendingDeleteCard = null;
    this.deleteSwipeDeltaY = 0;
  }

  confirmDelete(): void {
    if (!this.pendingDeleteCard) return;
    const card = this.pendingDeleteCard;
    const deleteData = this.buildDeleteData(card);
    this.costItemDelete.emit(deleteData);
    this.closeDeleteSheet();
  }

  private buildDeleteData(card: { itemName: string; unitCost: number; category: string; fullItem: any }): CostItemEditData {
    const base: CostItemEditData = { itemName: card.itemName, category: card.category, unitCost: card.unitCost };
    const f = card.fullItem;
    switch (card.category) {
      case 'Raw Material':
        return { ...base, inventoryId: f.inventoryId, bomComponentID: f.bomComponentID };
      case 'Direct Labor':
      case 'Indirect Labor':
        return { ...base, itemOperationID: f.itemOperationID };
      case 'Fixed Overhead':
        return { ...base, itemId: f.itemId };
      case 'Variable Overhead':
        return { ...base, inventoryId: f.inventoryId, variableOverheadItemID: f.variableOverheadItemID };
      case 'Competitor':
        return { ...base, competitorID: f.competitorID };
      default:
        return base;
    }
  }

  readonly Math = Math;

  // Markup bar state
  markupSectionOpen = true;
  productSectionOpen = true;
  // Single collapse for the whole sticky-top setup section (calculator tab)
  stickyTopOpen = true;
  private isKnobActive = false;

  // Local AI price overrides (updated from mobile Aivor sheet)
  localSuggestedPrice: number | null = null;
  localAiMarkupPrice: number | null = null;

  // Swipe tracking
  private touchStartX = 0;
  private touchStartY = 0;

  private sub!: Subscription;

  constructor(public mobileNavService: MobileNavService) {}

  ngOnInit(): void {
    this.sub = this.mobileNavService.activeTab.subscribe(
      (tab) => (this.activeTab = tab)
    );

    // Show/hide nav based on initial mobileView
    if (this.mobileView === 'calculator') {
      this.mobileNavService.showNav();
    } else {
      this.mobileNavService.hideNav();
    }

    // Debug: Log when inputs change
    console.log('Mobile Calc View - Initial Data:', {
      totalCost: this.totalCost,
      sellingPrice: this.sellingPrice,
      rawMaterialsCount: this.rawMaterials.length,
      directLaborCount: this.directLabor.length,
      productsCount: this.products.length
    });
  }

  // "Selling Price is R0" advisory (spec item 3), mobile equivalent of the
  // desktop popup in raw-materials.component. Fires only when the selected
  // product actually changes (not on every re-render), so it never appears
  // on initial page load via the parent's auto-restore of the last product.
  showZeroPricePopup = false;
  zeroPricePopupProductName = '';

  // "This service needs a rate" advisory, mobile equivalent of the desktop
  // popup in raw-materials.component. serviceLabourNeedsRate is computed
  // asynchronously (it depends on the direct-labour fetch), so it never
  // lands in the same change batch as activeProductId — arm on the product
  // change, fire once the flag catches up for that same product.
  showServiceNeedsRatePopup = false;
  serviceNeedsRatePopupProductName = '';
  private pendingServiceRatePromptFor: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeProductId'] && this.activeProductId != null) {
      this.selectedProductId = this.activeProductId;
      if (this.productSellingPrice === null || this.productSellingPrice === undefined || this.productSellingPrice === 0) {
        const prod = this.products?.find((p: any) => p.itemID === this.activeProductId);
        this.zeroPricePopupProductName = prod?.itemName ?? '';
        this.showZeroPricePopup = true;
      } else {
        this.showZeroPricePopup = false;
      }

      const selectedProduct = this.products?.find((p: any) => p.itemID === this.activeProductId);
      this.pendingServiceRatePromptFor = selectedProduct?.itemTypeID === 4 ? this.activeProductId : null;
      this.showServiceNeedsRatePopup = false;
    }

    if (
      changes['serviceLabourNeedsRate'] &&
      this.serviceLabourNeedsRate &&
      this.pendingServiceRatePromptFor === this.activeProductId
    ) {
      const prod = this.products?.find((p: any) => p.itemID === this.activeProductId);
      this.serviceNeedsRatePopupProductName = prod?.itemName ?? '';
      this.showServiceNeedsRatePopup = true;
      this.pendingServiceRatePromptFor = null;
    }

    if (changes['mobileView']) {
      if (this.mobileView === 'calculator') {
        this.mobileNavService.showNav();
      } else {
        this.mobileNavService.hideNav();
      }
    }

    this.mobileNavService.updateBadges({
      materials: this.rawMaterials.length,
      directLabour: this.directLabor.length,
      indirectLabour: this.indirectLabor.length,
      fixedOverheads: this.fixedOverheadNames?.length || this.fixedOverheads.length,
      variableOverheads: this.variableOverheads.length,
      competitors: this.competitors.length,
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  // ── Swipe handling ──────────────────────────────────────────────
  @HostListener('touchstart', ['$event'])
  onTouchStart(e: TouchEvent): void {
    const target = e.target as HTMLElement;
    if (target.closest('.single-select-dropdown') || target.closest('.dropdown-menu')) return;
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(e: TouchEvent): void {
    // Don't swipe-navigate when the touch originated inside a dropdown.
    const target = e.target as HTMLElement;
    if (target.closest('.single-select-dropdown') || target.closest('.dropdown-menu')) return;

    const dx = e.changedTouches[0].clientX - this.touchStartX;
    const dy = e.changedTouches[0].clientY - this.touchStartY;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) this.mobileNavService.nextTab();
      else this.mobileNavService.prevTab();
    }
  }

  // ── Card data helpers ────────────────────────────────────────────
  get activeCards(): { itemName: string; unitCost: number; category: string; fullItem: any }[] {
    switch (this.activeTab) {
      case 'materials':
        return this.rawMaterials.map((m: RawMaterial) => ({
          itemName: m.itemName,
          unitCost: m.unitCost ?? 0,
          category: 'Raw Material',
          fullItem: m,
        }));
      case 'directLabour':
        return this.directLabor.map((l) => ({
          itemName: l.employeeName || l.activity || 'Labour',
          unitCost: l.unitCost ?? 0,
          category: 'Direct Labor',
          fullItem: l,
        }));
      case 'indirectLabour':
        return this.indirectLabor.map((l) => ({
          itemName: l.employeeName || l.activity || 'Indirect Labour',
          unitCost: l.unitCost ?? 0,
          category: 'Indirect Labor',
          fullItem: l,
        }));
      case 'fixedOverheads':
        return this.mergedFixedOverheadCards;
      case 'variableOverheads':
        return this.variableOverheads.map((v) => ({
          itemName: v.itemName,
          unitCost: v.unitCost ?? 0,
          category: 'Variable Overhead',
          fullItem: v,
        }));
      case 'competitors':
        return this.competitors.map((c) => ({
          itemName: c.name ?? c.competitorName ?? 'Competitor',
          unitCost: c.price ?? c.competitorPrice ?? 0,
          category: 'Competitor',
          fullItem: c,
        }));
      default:
        return [];
    }
  }

  get mergedFixedOverheadCards(): { itemName: string; unitCost: number; category: string; fullItem: any }[] {
    // Min/Batch + Batch Size are shared per product across all fixed overheads.
    // Seed placeholder cards with the product's current shared values so they
    // display real values and the edit offcanvas pre-fills correctly.
    const shared = this.fixedOverheads.find((f: any) => !f.isPlaceholder);
    const sharedMinutes = shared?.minutesPerOrder ?? 0;
    const sharedBatch = shared?.batchSize ?? 0;
    return this.fixedOverheadNames.map((name: any) => {
      const existing = this.fixedOverheads.find(
        (f: any) => f.overheadCostItemID === name.overheadCostItemID
      );
      if (existing) {
        return {
          itemName: existing.itemName,
          unitCost: existing.unitCost ?? 0,
          category: 'Fixed Overhead',
          fullItem: existing,
        };
      }
      const ratePerMinute = name.ratePerMinute ?? 0;
      const unitCost =
        sharedMinutes && ratePerMinute && sharedBatch > 0
          ? +((sharedMinutes * ratePerMinute) / sharedBatch).toFixed(4)
          : 0;
      return {
        itemName: name.overheadName,
        unitCost,
        category: 'Fixed Overhead',
        fullItem: {
          itemName: name.overheadName,
          overheadCostItemID: name.overheadCostItemID,
          itemId: null,
          isPlaceholder: true,
          costPerMonth: name.costPerMonth ?? 0,
          hoursOpenPerMonth: name.hoursOpenPerMonth ?? 0,
          ratePerMinute,
          minutesPerOrder: sharedMinutes,
          batchSize: sharedBatch,
          unitCost,
        },
      };
    });
  }

  get metrics(): MobileMetric[] {
    const fmt = (n: number) =>
      new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(n);
    return [
      { label: 'Total Cost', value: fmt(this.totalCost) },
      { label: 'Selling Price', value: fmt(this.productSellingPrice) },
      { label: 'AI Suggested', value: fmt(this.localSuggestedPrice ?? this.suggestedSellingPriceRaw) },
      { label: 'Calc Price', value: fmt(this.calculatorSellingPrice) },
      { label: 'AI Advised', value: fmt(this.localAiMarkupPrice ?? this.aiSuggestedPrice) },
      { label: 'Markup', value: fmt(this.markupPrice) },
    ];
  }

  getCardFields(card: { itemName: string; unitCost: number; category: string; fullItem: any }): MobileCardField[] {
    const fmt = (n: number) =>
      new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(n);
    const item = card.fullItem;
    const fields: MobileCardField[] = [
      { label: 'Unit Cost', value: fmt(card.unitCost), chip: true },
    ];

    switch (card.category) {
      case 'Raw Material': {
        const purchaseQty = item?.purchaseQuantity ?? 0;
        const purchaseUnit = item?.purchaseUnit ? ` ${item.purchaseUnit}` : '';
        const usageUnit = item?.usageUnit ? ` ${item.usageUnit}` : '';
        fields.push({ label: 'Purchase Price', value: fmt(item?.purchasePrice ?? 0) });
        fields.push({ label: 'Purchase Qty', value: `${purchaseQty}${purchaseUnit}` });
        fields.push({ label: 'Price/Unit', value: `${fmt(item?.purchasePricePerUnit ?? 0)}${usageUnit || purchaseUnit}` });
        fields.push({ label: 'Usage Qty', value: `${item?.usageQuantity ?? 0}${usageUnit}` });
        fields.push({ label: 'Batch Size', value: item?.batchSize ?? 0 });
        break;
      }
      case 'Direct Labor':
        fields.push({ label: 'Role', value: item?.roleDescription ?? '' });
        fields.push({ label: 'Salary (R)', value: fmt(item?.monthlySalary ?? 0) });
        fields.push({ label: 'Hrs Wrkd', value: item?.hoursWorkedPm ?? 0 });
        fields.push({ label: 'Rate Type', value: item?.rateType ?? 'Per Minute' });
        fields.push({ label: 'Rate/min (R)', value: fmt(item?.ratePerMinute ?? 0) });
        fields.push({ label: 'Activity', value: item?.activity ?? '' });
        fields.push({ label: 'Min/Act', value: item?.minutesPerJob ?? 0 });
        fields.push({ label: 'Batch Size', value: item?.batchSize ?? 0 });
        break;
      case 'Indirect Labor':
        fields.push({ label: 'Role', value: item?.roleDescription ?? '' });
        fields.push({ label: 'Salary PM (R)', value: fmt(item?.monthlySalary ?? 0) });
        fields.push({ label: 'Hrs PM', value: item?.hoursWorkedPm ?? 0 });
        fields.push({ label: 'Rate Type', value: item?.rateType ?? 'Per Minute' });
        fields.push({ label: 'Rate/min (R)', value: fmt(item?.ratePerMinute ?? 0) });
        fields.push({ label: 'Activity', value: item?.activity ?? '' });
        fields.push({ label: 'Min/Activity', value: item?.minutesPerActivity ?? 0 });
        fields.push({ label: 'Batch Size', value: item?.batchSize ?? 0 });
        break;
      case 'Fixed Overhead':
        fields.push({ label: 'Cost PM (R)', value: fmt(item?.costPerMonth ?? 0) });
        fields.push({ label: 'Hrs Open PM', value: item?.hoursOpenPerMonth ?? 0 });
        fields.push({ label: 'Rate/min (R)', value: fmt(item?.ratePerMinute ?? 0) });
        fields.push({ label: 'Min/Batch', value: item?.minutesPerOrder ?? 0 });
        fields.push({ label: 'Batch Size', value: item?.batchSize ?? 0 });
        break;
      case 'Variable Overhead': {
        const vPurchaseUnit = item?.purchaseUnit ? ` ${item.purchaseUnit}` : '';
        const vUsageUnit = item?.usageUnit ? ` ${item.usageUnit}` : '';
        fields.push({ label: 'Purch. Price', value: fmt(item?.purchasePrice ?? 0) });
        fields.push({ label: 'Purch. Qty', value: `${item?.purchaseQuantity ?? 0}${vPurchaseUnit}` });
        fields.push({ label: 'Price/Unit', value: fmt(item?.purchasePricePerUnit ?? 0) });
        fields.push({ label: 'Usage Qty', value: `${item?.usageQuantity ?? 0}${vUsageUnit}` });
        fields.push({ label: 'Batch Size', value: item?.batchSize ?? 0 });
        break;
      }
      case 'Competitor':
        fields.push({ label: 'Website', value: item?.website ?? '' });
        fields.push({ label: 'Is Active', value: item?.isActive ? 'Yes' : 'No' });
        fields.push({ label: 'Target Market Segment', value: item?.targetMarketSegment ?? '' });
        fields.push({ label: 'Primary Sales Channel', value: item?.primarySalesChannel ?? '' });
        fields.push({ label: 'Geographic Focus', value: item?.geographicFocus ?? '' });
        break;
    }
    return fields;
  }

  openOffcanvas(card: { itemName: string; unitCost: number; category: string; fullItem: any }): void {
    // Create base item data
    const baseItem = {
      itemName: card.itemName,
      category: card.category,
      unitCost: card.unitCost,
    };

    // Add category-specific fields from the full item
    const fullItem = card.fullItem;
    if (card.category === 'Raw Material') {
      this.selectedItem = {
        ...baseItem,
        inventoryId: fullItem.inventoryId,
        usageQuantity: fullItem.usageQuantity,
        batchSize: fullItem.batchSize,
        purchasePrice: fullItem.purchasePrice,
        purchaseQuantity: fullItem.purchaseQuantity,
        purchaseUnit: fullItem.purchaseUnit,
        usageUnit: fullItem.usageUnit,
      };
    } else if (card.category === 'Direct Labor') {
      this.selectedItem = {
        ...baseItem,
        itemName: fullItem.activity || '',
        itemOperationID: fullItem.itemOperationID,
        employeeID: fullItem.employeeID,
        monthlySalary: fullItem.monthlySalary,
        hoursWorkedPm: fullItem.hoursWorkedPm,
        minutesPerJob: fullItem.minutesPerJob,
        batchSize: fullItem.batchSize,
        roleDescription: fullItem.roleDescription,
        rateType: fullItem.rateType,
        ratePerMinute: fullItem.ratePerMinute,
      };
    } else if (card.category === 'Indirect Labor') {
      this.selectedItem = {
        ...baseItem,
        itemName: fullItem.activity || '',
        itemOperationID: fullItem.itemOperationID,
        employeeID: fullItem.employeeID,
        monthlySalary: fullItem.monthlySalary,
        hoursWorkedPm: fullItem.hoursWorkedPm,
        minutesPerActivity: fullItem.minutesPerActivity,
        batchSize: fullItem.batchSize,
        roleDescription: fullItem.roleDescription,
        rateType: fullItem.rateType,
        ratePerMinute: fullItem.ratePerMinute,
      };
    } else if (card.category === 'Fixed Overhead') {
      this.selectedItem = {
        ...baseItem,
        itemId: fullItem.isPlaceholder ? null : fullItem.itemId,
        overheadCostItemID: fullItem.overheadCostItemID,
        costPerMonth: fullItem.costPerMonth,
        hoursOpenPerMonth: fullItem.hoursOpenPerMonth,
        minutesPerOrder: fullItem.minutesPerOrder,
        batchSize: fullItem.batchSize,
      };
    } else if (card.category === 'Variable Overhead') {
      this.selectedItem = {
        ...baseItem,
        inventoryId: fullItem.inventoryId,
        purchasePrice: fullItem.purchasePrice,
        purchaseQuantity: fullItem.purchaseQuantity,
        purchasePricePerUnit: fullItem.purchasePricePerUnit,
        usageQuantity: fullItem.usageQuantity,
        batchSize: fullItem.batchSize,
        estimatedSales: fullItem.estimatedSales,
        monthlyCost: fullItem.monthlyCost,
      };
    } else if (card.category === 'Competitor') {
      const isFirst = this.competitors.indexOf(card.fullItem) === 0;
      this.selectedItem = {
        ...baseItem,
        competitorID: fullItem.competitorID,
        price: isFirst ? this.sellingPrice : (fullItem.price ?? fullItem.competitorPrice),
        website: fullItem.website,
        isActive: fullItem.isActive ?? true,
        targetMarketSegment: fullItem.targetMarketSegment ?? fullItem.target_market_segment,
        primarySalesChannel: fullItem.primarySalesChannel ?? fullItem.primary_sales_channel,
        geographicFocus: fullItem.geographicFocus ?? fullItem.geographic_focus,
        isTargetSellingPrice: isFirst,
      };
    } else {
      this.selectedItem = baseItem;
    }

    this.offcanvasVisible = true;
  }

  openAddItemDialog(): void {
    if (!this.targetTotalCost || this.targetTotalCost <= 0) {
      this.showTotalCostAlert = true;
      return;
    }
    this.showTotalCostAlert = false;

    // Create a new item based on the active tab
    const categoryMap: { [key: string]: string } = {
      'materials': 'Raw Material',
      'directLabour': 'Direct Labor',
      'indirectLabour': 'Indirect Labor',
      'fixedOverheads': 'Fixed Overhead',
      'variableOverheads': 'Variable Overhead',
      'competitors': 'Competitor',
    };

    this.selectedItem = {
      itemName: '',
      category: categoryMap[this.activeTab] || 'Raw Material',
      unitCost: 0,
    };
    this.offcanvasVisible = true;
  }

  onTargetCostChange(val: string): void {
    const parsed = val === '' ? null : parseFloat(val);
    this.targetCostChange.emit(isNaN(parsed as number) ? null : parsed);
  }

  onProductChange(): void {
    if (this.selectedProductId) {
      this.productChange.emit(+this.selectedProductId);
    }
  }

  onProductDropdownChange(val: string | null): void {
    this.selectedProductId = val ? +val : null;
    this.onProductChange();
  }

  onAddNewProduct(): void {
    this.selectedItem = {
      itemName: '',
      category: 'Product',
      unitCost: 0,
      sellingPrice: 0,
      description: '',
    };
    this.offcanvasVisible = true;
    this.addProduct.emit();
  }

  onMarginChange(): void {
    // Handle margin percentage change
    console.log('Margin changed to:', this.profitMarginPercentage);
    this.marginChange.emit(this.profitMarginPercentage);
  }

  increaseMargin(): void {
    if (this.profitMarginPercentage < 100) {
      this.profitMarginPercentage += 1;
      this.onMarginChange();
    }
  }

  decreaseMargin(): void {
    if (this.profitMarginPercentage > 0) {
      this.profitMarginPercentage -= 1;
      this.onMarginChange();
    }
  }

  closeOffcanvas(): void {
    this.offcanvasVisible = false;
    this.selectedItem = null;
  }

  onItemSaved(data: CostItemEditData): void {
    if (data.category === 'Product' && data.sellingPrice != null) {
      this.productSellingPrice = data.sellingPrice;
    }
    this.costItemUpsert.emit(data);
    this.closeOffcanvas();
  }

  onSaveAll(): void {
    // Emit save all event to parent component
    this.saveAll.emit();
  }

  onFOHSaveAll(): void {
    this.fohSaveAll.emit();
  }

  trackCard(_index: number, card: { itemName: string; category: string; fullItem: any }): string {
    return card.category + '|' + (card.fullItem?.itemOperationID ?? card.fullItem?.inventoryId ?? card.fullItem?.itemId ?? card.fullItem?.variableOverheadItemID ?? card.fullItem?.competitorID ?? card.itemName);
  }

  closeZeroPricePopup(): void {
    this.showZeroPricePopup = false;
  }

  closeServiceNeedsRatePopup(): void {
    this.showServiceNeedsRatePopup = false;
  }

  goToLabourFromServicePopup(): void {
    this.showServiceNeedsRatePopup = false;
    this.mobileNavService.setActiveTab('directLabour');
  }

  setPriceNowFromZeroPopup(): void {
    this.showZeroPricePopup = false;
    this.openProductEdit();
  }

  openProductEdit(): void {
    if (!this.selectedProductId) return;
    const prod = this.products.find((p: any) => p.itemID === this.selectedProductId);
    if (!prod) return;
    this.selectedItem = {
      itemName: prod.itemName ?? '',
      category: 'Product',
      unitCost: 0,
      inventoryId: prod.itemID,
      sellingPrice: prod.sellingPrice ?? 0,
      description: prod.description ?? '',
    };
    this.offcanvasVisible = true;
  }

  startEditSellingPrice(): void {
    this.editSellingPriceValue = this.productSellingPrice;
    this.editingSellingPrice = true;
  }

  cancelEditSellingPrice(): void {
    this.editingSellingPrice = false;
  }

  confirmEditSellingPrice(): void {
    const val = Number(this.editSellingPriceValue);
    if (!isNaN(val) && val >= 0) {
      this.sellingPriceChange.emit(val);
    }
    this.editingSellingPrice = false;
  }

  toggleMarkupSection(): void {
    this.markupSectionOpen = !this.markupSectionOpen;
  }

  toggleProductSection(): void {
    this.productSectionOpen = !this.productSectionOpen;
  }

  toggleStickyTop(): void {
    this.stickyTopOpen = !this.stickyTopOpen;
  }

  onAiSuggestedPriceChange(val: number): void {
    this.localSuggestedPrice = val;
  }

  onAiMarkupPriceChange(val: number): void {
    this.localAiMarkupPrice = val;
  }

  get markupFillPercent(): number {
    return (this.profitMarginPercentage / 300) * 100;
  }

  private setMarkupFromClientX(clientX: number, rect: DOMRect): void {
    const fraction = (clientX - rect.left) / rect.width;
    this.profitMarginPercentage = Math.max(0, Math.min(300, Math.round(fraction * 300)));
    this.onMarginChange();
  }

  onBarPointerDown(event: MouseEvent): void {
    this.isKnobActive = true;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.setMarkupFromClientX(event.clientX, rect);
  }

  onKnobTouchStart(event: TouchEvent): void {
    event.preventDefault();
    this.isKnobActive = true;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.setMarkupFromClientX(event.touches[0].clientX, rect);
  }

  onKnobTouchMove(event: TouchEvent): void {
    if (!this.isKnobActive) return;
    event.preventDefault();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.setMarkupFromClientX(event.touches[0].clientX, rect);
  }

  onKnobTouchEnd(event: TouchEvent): void {
    event.preventDefault();
    this.isKnobActive = false;
  }

  getKnobColor(): string {
    if (this.profitMarginPercentage <= 100) return '#5bc4d4';
    if (this.profitMarginPercentage <= 200) return '#007a8f';
    return '#004f62';
  }

  // ── Summary helpers ──────────────────────────────────────────────
  expandedProducts: { [key: string]: boolean } = {};
  showAllSummaryProducts = false;

  private get resolvedSelectedName(): string {
    // Try explicit inputs first, then fall back to matching by selectedProductId
    const name = this.selectedProductName || this.productName;
    if (name && this.allProductCosts && this.allProductCosts[name]) return name;

    // Fall back: find product name from products list by selectedProductId
    if (this.selectedProductId && this.products?.length) {
      const prod = this.products.find((p: any) => p.itemID === +this.selectedProductId!);
      if (prod?.itemName && this.allProductCosts?.[prod.itemName]) return prod.itemName;
    }
    return name || '';
  }

  get selectedSummaryEntry(): { key: string; value: CombinedCostItem[] } | null {
    const name = this.resolvedSelectedName;
    if (!name || !this.allProductCosts) return null;
    const value = this.allProductCosts[name];
    return value ? { key: name, value } : null;
  }

  get otherSummaryEntries(): Array<{ key: string; value: CombinedCostItem[] }> {
    const name = this.resolvedSelectedName;
    return this.mapToKeys(this.allProductCosts).filter(e => e.key !== name);
  }

  toggleExpand(productKey: string): void {
    this.expandedProducts[productKey] = !this.expandedProducts[productKey];
  }

  mapToKeys(obj: Record<string, CombinedCostItem[]> | null): Array<{ key: string; value: CombinedCostItem[] }> {
    if (!obj) return [];
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  getProductTotal(costs: CombinedCostItem[]): number {
    return costs.reduce((sum, c) => sum + c.unitCost, 0);
  }

}
